import { useCallback, useRef, useState } from "react";

import { QuestionType } from "@simplyk/common";

import { trpc } from "@/common/helpers/trpc";
import { TicketingOutput } from "@/common/types/trpc";

export interface SmsOptInConfirmation {
  isOpen: boolean;
  guardSetSmsEnabled: (rawSetSmsEnabled: (value: boolean) => void) => (value: boolean) => void;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export const useSmsOptInConfirmation = (apiTicketing: TicketingOutput): SmsOptInConfirmation => {
  const [pendingSetter, setPendingSetter] = useState<(() => void) | null>(null);
  const { mutateAsync: addNotificationPhoneNumberQuestion } = trpc.addNotificationPhoneNumberQuestion.useMutation();
  const utils = trpc.useUtils();

  const { data: questionsData } = trpc.getTicketingQuestionsStepData.useQuery({ ticketingId: apiTicketing.id });
  const queryHasPhoneQuestion = (questionsData?.questions ?? []).some(
    (question) => question.type === QuestionType.NotificationPhoneNumber
  );
  const justAddedRef = useRef(false);
  if (queryHasPhoneQuestion) {
    justAddedRef.current = false;
  }
  const hasPhoneQuestion = queryHasPhoneQuestion || justAddedRef.current;

  const guardSetSmsEnabled = useCallback(
    (rawSetSmsEnabled: (value: boolean) => void) => (value: boolean) => {
      if (!value || hasPhoneQuestion) {
        rawSetSmsEnabled(value);
        return;
      }
      setPendingSetter(() => () => rawSetSmsEnabled(true));
    },
    [hasPhoneQuestion]
  );

  const onConfirm = useCallback(async () => {
    if (!pendingSetter) {
      return;
    }
    await addNotificationPhoneNumberQuestion({ ticketingId: apiTicketing.id });
    justAddedRef.current = true;
    await Promise.all([
      utils.form_getTicketingFrontendPage.invalidate(),
      utils.getTicketingQuestionsStepData.invalidate({ ticketingId: apiTicketing.id }),
    ]);
    pendingSetter();
    setPendingSetter(null);
  }, [pendingSetter, addNotificationPhoneNumberQuestion, apiTicketing.id, utils]);

  const onCancel = useCallback(() => setPendingSetter(null), []);

  return { isOpen: pendingSetter !== null, guardSetSmsEnabled, onConfirm, onCancel };
};
